import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { AppContext } from "../context/AppContext";
import { Box, ButtonBase, Dialog, IconButton, InputBase, Typography } from "@mui/material";
import { CheckCircle, Close, InfoOutlined } from "@mui/icons-material";
import { OwnerConsoleViewController } from "../controllers/OwnerConsoleViewController";
import { AdjustmentIcon } from "../helpers/adjustmentIcon";

const INK = "#1d2433";
const INK_2 = "#4a5263";
const MUTED = "#6b7385";
const LINE = "#e1e4ea";

// "#16B380" -> "rgba(22, 179, 128, 0.08)"
function withAlpha(hex: string, alpha: number) {
	const match = /^#?([0-9a-f]{6})$/i.exec(hex ?? "");
	if (!match) return `rgba(0, 0, 0, ${alpha})`;
	const n = parseInt(match[1], 16);
	return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

// Keeps digits only and adds thousands separators, so typing letters can never show "NaN".
function formatAmount(value: string) {
	const digits = value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 9);
	return digits ? Number(digits).toLocaleString("en-US") : "";
}

// Form for a homeowner to tell us about work done on their home (a new kitchen, a deck...).
//
// The report is often embedded in an iframe as tall as the whole report, so "fixed to the bottom" or
// "centered" means somewhere the homeowner may not be looking. Instead the form opens centered on
// the button they tapped (`anchor`), kept inside the frame.
export function NewAdjustment(props: { show: boolean; toggleModal: (open: boolean) => void; anchor?: DOMRect | null }) {
	const { show, toggleModal, anchor } = props;
	const { appState, appDispatch } = useContext(AppContext);
	const { report_configuration, owner_adjustment_types, currentAddrId, colorPallete } = appState;
	const { owner_adjustments, addrId } = report_configuration.find((x) => x.addrId === currentAddrId) ?? {};
	const brand = colorPallete["scheme1"];

	const [newAdjustmentType, setNewAdjustmentType] = useState(-1);
	const [newAdjustmentValue, setNewAdjustmentValue] = useState("");

	const existing_adjustment_types = owner_adjustments?.map((_) => _.adjustment_type_id) ?? [];
	const types = owner_adjustment_types.filter((option) => option.adjustment_type_id !== -1);
	const canSubmitAdjustment = newAdjustmentType !== -1 && newAdjustmentValue !== "";

	// Start fresh every time the form opens.
	useEffect(() => {
		if (show) {
			setNewAdjustmentType(-1);
			setNewAdjustmentValue("");
		} else {
			setTop(null);
		}
	}, [show]);

	// Place the form on the tapped button once its height is known. It stays hidden until then so it
	// never flashes in the wrong spot. Focus moves into it without scrolling the page.
	const [paperEl, setPaperEl] = useState<HTMLDivElement | null>(null);
	const [top, setTop] = useState<number | null>(null);
	useLayoutEffect(() => {
		if (!show || !paperEl) return;
		const gap = 12;
		const height = paperEl.offsetHeight;
		const target = anchor ? anchor.top + anchor.height / 2 : window.innerHeight / 2;
		setTop(Math.max(gap, Math.min(target - height / 2, window.innerHeight - height - gap)));
		paperEl.focus({ preventScroll: true });
	}, [show, paperEl, anchor]);

	async function handleAddNewAdjustment() {
		if (!canSubmitAdjustment) return;
		try {
			toggleModal(false);
			appDispatch({ type: "SET_FETCHING_ADJUSTMENTS", value: true });
			await OwnerConsoleViewController.AddNewAdjustment({
				adjustment_type: newAdjustmentType,
				adjustment_value: newAdjustmentValue.replace(/,/g, ""),
				addrId: addrId,
				enc: appState.enc,
				track: appState.trackView,
			});
			const { report_configuration } = await OwnerConsoleViewController.GetReportConfiguration(
				appState.enc ?? "",
			);
			if (report_configuration) {
				appDispatch({
					type: "UPDATE_STATE",
					key: "report_configuration",
					value: report_configuration,
				});
			}
		} finally {
			appDispatch({ type: "SET_FETCHING_ADJUSTMENTS", value: false });
			toggleModal(false);
		}
	}

	return (
		<Dialog
			open={!!show}
			onClose={() => toggleModal(false)}
			aria-labelledby="new-adjustment-title"
			// Default focus handling focuses the full-frame dialog root, which scrolls a tall iframe's
			// host page to the top; focus goes to the form itself instead (see above).
			disableAutoFocus
			disableEnforceFocus
			slotProps={{ backdrop: { sx: { backgroundColor: "rgba(17, 24, 39, .45)", backdropFilter: "blur(4px)" } } }}
			sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
			PaperProps={{
				ref: setPaperEl,
				tabIndex: -1,
				style: { marginTop: top ?? 0, visibility: top === null ? "hidden" : "visible" },
				sx: {
					mx: { xs: "12px", sm: "auto" },
					mb: "12px",
					width: { xs: "calc(100% - 24px)", sm: 480 },
					maxWidth: { xs: "calc(100% - 24px)", sm: 480 },
					maxHeight: "calc(100% - 24px)",
					borderRadius: "18px",
					outline: "none",
					border: "none",
					boxShadow: "0 24px 64px rgba(17, 24, 39, .22)",
					color: INK,
					textAlign: "left",
				},
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
				<Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px", p: "20px 20px 4px 24px" }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							id="new-adjustment-title"
							component="h2"
							sx={{ fontSize: "20px", fontWeight: 700, letterSpacing: "-.01em", color: INK }}
						>
							Add a home update
						</Typography>
						<Typography sx={{ fontSize: "14px", color: MUTED, mt: "4px", lineHeight: 1.5 }}>
							Tell us about work you've done and we'll factor it into your estimate.
						</Typography>
					</Box>
					<IconButton aria-label="Close" onClick={() => toggleModal(false)} sx={{ color: MUTED, mt: "-4px" }}>
						<Close />
					</IconButton>
				</Box>

				<Box sx={{ overflowY: "auto", p: "16px 24px 8px" }}>
					<Typography
						id="adjustment-type-label"
						sx={{ fontSize: "13px", fontWeight: 700, color: INK, mb: "10px" }}
					>
						What did you update?
					</Typography>
					<Box
						role="radiogroup"
						aria-labelledby="adjustment-type-label"
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
							gap: "8px",
						}}
					>
						{types.map((option) => {
							const added = existing_adjustment_types.includes(option.adjustment_type_id);
							const selected = newAdjustmentType === option.adjustment_type_id;
							return (
								<ButtonBase
									key={option.adjustment_type_id}
									role="radio"
									aria-checked={selected}
									disabled={added}
									disableRipple
									onClick={() => setNewAdjustmentType(option.adjustment_type_id)}
									sx={{
										position: "relative",
										display: "flex",
										// Icon beside the name on phones (shorter tiles), above it on larger screens.
										flexDirection: { xs: "row", sm: "column" },
										alignItems: { xs: "center", sm: "flex-start" },
										justifyContent: "flex-start",
										gap: { xs: "10px", sm: "6px" },
										p: { xs: "10px 28px 10px 12px", sm: "12px" },
										minHeight: { xs: 48, sm: 0 },
										borderRadius: "12px",
										textAlign: "left",
										border: `1.5px solid ${selected ? brand : LINE}`,
										backgroundColor: selected ? withAlpha(brand, 0.06) : "#fff",
										color: selected ? brand : INK,
										transition: "border-color .15s, background-color .15s",
										"&:hover": { borderColor: selected ? brand : "#c9ced8" },
										"&.Mui-focusVisible": { outline: `2px solid ${brand}`, outlineOffset: "2px" },
										"&.Mui-disabled": { opacity: 0.5, backgroundColor: "#f5f7fa" },
									}}
								>
									<AdjustmentIcon type={option.adjustment_type} sx={{ fontSize: 22, flex: "none" }} />
									<Box
										component="span"
										sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}
									>
										<Box
											component="span"
											sx={{ fontSize: "13px", fontWeight: 600, lineHeight: 1.25 }}
										>
											{option.adjustment_type}
										</Box>
										{added && (
											<Box component="span" sx={{ fontSize: "11px", color: MUTED }}>
												Already added
											</Box>
										)}
									</Box>
									{selected && (
										<CheckCircle
											sx={{ position: "absolute", top: 8, right: 8, fontSize: 18, color: brand }}
										/>
									)}
								</ButtonBase>
							);
						})}
					</Box>

					<Typography
						component="label"
						htmlFor="adjustment-amount"
						sx={{ display: "block", fontSize: "13px", fontWeight: 700, color: INK, mt: "20px", mb: "8px" }}
					>
						About how much did it cost?
					</Typography>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "6px",
							px: "14px",
							height: 52,
							borderRadius: "12px",
							border: `1.5px solid ${LINE}`,
							transition: "border-color .15s, box-shadow .15s",
							"&:focus-within": { borderColor: brand, boxShadow: `0 0 0 3px ${withAlpha(brand, 0.15)}` },
						}}
					>
						<Box component="span" sx={{ fontSize: "18px", fontWeight: 600, color: MUTED }}>
							$
						</Box>
						<InputBase
							id="adjustment-amount"
							value={newAdjustmentValue}
							onChange={(e) => setNewAdjustmentValue(formatAmount(e.target.value))}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleAddNewAdjustment();
							}}
							placeholder="25,000"
							inputProps={{
								inputMode: "numeric",
								autoComplete: "off",
								"aria-describedby": "adjustment-amount-hint",
							}}
							sx={{
								flex: 1,
								fontSize: "18px",
								fontWeight: 600,
								color: INK,
								fontVariantNumeric: "tabular-nums",
							}}
						/>
					</Box>
					<Typography id="adjustment-amount-hint" sx={{ fontSize: "12px", color: MUTED, mt: "6px" }}>
						A rough number is fine.
					</Typography>
				</Box>

				<Box
					sx={{
						p: "16px 24px 20px",
						borderTop: `1px solid ${LINE}`,
						mt: "12px",
					}}
				>
					<Typography
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "6px",
							fontSize: "12px",
							color: INK_2,
							mb: "12px",
						}}
					>
						<InfoOutlined sx={{ fontSize: 16, color: MUTED }} />
						Your estimate updates as soon as you save.
					</Typography>
					<Box sx={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
						<ButtonBase
							onClick={() => toggleModal(false)}
							sx={{
								px: "18px",
								height: 44,
								borderRadius: "10px",
								fontSize: "14px",
								fontWeight: 600,
								color: INK_2,
								flex: { xs: 1, sm: "none" },
								"&:hover": { backgroundColor: "#f1f3f7" },
							}}
						>
							Cancel
						</ButtonBase>
						<ButtonBase
							onClick={handleAddNewAdjustment}
							disabled={!canSubmitAdjustment}
							sx={{
								px: "20px",
								height: 44,
								borderRadius: "10px",
								fontSize: "14px",
								fontWeight: 700,
								color: "#fff",
								backgroundColor: brand,
								flex: { xs: 2, sm: "none" },
								"&:hover": { filter: "brightness(.94)" },
								"&.Mui-focusVisible": { outline: `2px solid ${brand}`, outlineOffset: "2px" },
								"&.Mui-disabled": { backgroundColor: "#e6e9ef", color: "#9aa1ae" },
							}}
						>
							Save update
						</ButtonBase>
					</Box>
				</Box>
			</Box>
		</Dialog>
	);
}
